import fs from 'fs-extra'
import path from 'path'
import { Logger } from '../utils/logger'
import { legalizeWeappCss } from '../utils/weapp-postcss-legalizer'
import { escapeWeappClass } from '../utils/escape-weapp-class'

interface AutoPatchOptions {
  platform: 'weapp' | 'h5'
  verbose: boolean
  enableRuntime: boolean
  enableClassPatch: boolean
  enableSelectorPatch: boolean
  enableAutoPatch: boolean
}

export class AutoPatcher {
  private logger: Logger
  private options: AutoPatchOptions

  constructor(options: AutoPatchOptions) {
    this.options = options
    this.logger = new Logger(options.verbose)
  }

  /**
   * 自动检测并处理构建产物
   */
  async autoPatch(): Promise<void> {
    if (!this.options.enableAutoPatch) {
      return
    }

    this.logger.info('🔍 自动检测构建产物...')

    // 检测可能的构建目录
    const possibleDirs = [
      'dist',
      'build',
      'output',
      'miniprogram',
      'weapp'
    ]

    for (const dir of possibleDirs) {
      if (await fs.pathExists(dir)) {
        this.logger.info(`📁 发现构建目录: ${dir}`)
        await this.processBuildOutput(dir)
        break
      }
    }
  }

  /**
   * 处理构建输出
   */
  private async processBuildOutput(buildDir: string): Promise<void> {
    const startTime = Date.now()
    let processedFiles = 0
    let totalSize = 0
    let originalSize = 0

    try {
      // 处理 WXSS 文件
      if (this.options.platform === 'weapp') {
        const wxssFiles = await this.findWxssFiles(buildDir)
        for (const file of wxssFiles) {
          await this.processWxssFile(file, buildDir, {
            processedFiles,
            totalSize,
            originalSize
          })
        }
      }

      // 处理类名
      if (this.options.enableClassPatch) {
        await this.processClassNames(buildDir)
      }

      // 处理选择器
      if (this.options.enableSelectorPatch) {
        await this.processSelectors(buildDir)
      }

      // 添加运行时支持
      if (this.options.enableRuntime) {
        await this.addRuntimeSupport(buildDir)
      }

      const totalTime = Date.now() - startTime
      this.logger.success('🎉 自动 patch 完成！')
      this.logger.info(`⏱️ 总时间: ${totalTime}ms`)

    } catch (error) {
      this.logger.error('❌ 自动 patch 失败:', error)
    }
  }

  /**
   * 处理单个 WXSS 文件
   */
  private async processWxssFile(
    filePath: string,
    buildDir: string,
    stats: { processedFiles: number; totalSize: number; originalSize: number }
  ): Promise<void> {
    try {
      const originalCss = await fs.readFile(filePath, 'utf-8')
      const legalCss = legalizeWeappCss(originalCss)

      await fs.writeFile(filePath, legalCss)

      stats.processedFiles++
      stats.originalSize += originalCss.length
      stats.totalSize += legalCss.length

      this.logger.success(`📦 处理文件: ${path.relative(buildDir, filePath)}`)
      this.logger.info(`📏 原始大小: ${originalCss.length} 字符`)
      this.logger.info(`📏 处理后: ${legalCss.length} 字符`)
      this.logger.info(`📈 压缩率: ${((1 - legalCss.length / originalCss.length) * 100).toFixed(2)}%`)

    } catch (error) {
      this.logger.error(`❌ 处理文件失败: ${filePath}`, error)
    }
  }

  /**
   * 处理类名
   */
  private async processClassNames(buildDir: string): Promise<void> {
    const jsFiles = await this.findJsFiles(buildDir)

    for (const file of jsFiles) {
      try {
        let content = await fs.readFile(file, 'utf-8')
        const originalContent = content

        content = this.fixRemovedClassNames(content)

        if (content !== originalContent) {
          await fs.writeFile(file, content)
          this.logger.success(`🔧 修复类名: ${path.relative(buildDir, file)}`)
        }
      } catch (error) {
        this.logger.error(`❌ 处理类名失败: ${file}`, error)
      }
    }
  }

  /**
   * 处理选择器
   */
  private async processSelectors(buildDir: string): Promise<void> {
    const wxssFiles = await this.findWxssFiles(buildDir)

    for (const file of wxssFiles) {
      try {
        let content = await fs.readFile(file, 'utf-8')
        const originalContent = content

        content = this.fixSelectorCompatibility(content)

        if (content !== originalContent) {
          await fs.writeFile(file, content)
          this.logger.success(`🔧 修复选择器: ${path.relative(buildDir, file)}`)
        }
      } catch (error) {
        this.logger.error(`❌ 处理选择器失败: ${file}`, error)
      }
    }
  }

  /**
   * 添加运行时支持
   */
  private async addRuntimeSupport(buildDir: string): Promise<void> {
    const runtimeScript = `
// TailwindCSS Taro 运行时支持
(function() {
  // 类名转义函数
  window.escapeWeappClass = function(className) {
    return className.replace(/[^a-zA-Z0-9-_]/g, '_');
  };
  
  // 运行时类名注入
  window.injectTailwindClass = function(element, className) {
    if (element && element.className) {
      const escapedClass = window.escapeWeappClass(className);
      element.className += ' ' + escapedClass;
    }
  };
  
  // 自动处理动态类名
  window.autoPatchClassNames = function() {
    const elements = document.querySelectorAll('[class]');
    elements.forEach(element => {
      const classes = element.className.split(' ');
      const escapedClasses = classes.map(cls => window.escapeWeappClass(cls));
      element.className = escapedClasses.join(' ');
    });
  };
  
  // 监听 DOM 变化
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          window.autoPatchClassNames();
        }
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  
  console.log('🎨 TailwindCSS Taro 运行时支持已加载');
})();
`

    const runtimeFile = path.join(buildDir, 'tailwindcss-taro-runtime.js')
    await fs.writeFile(runtimeFile, runtimeScript)
    this.logger.success('🔧 添加运行时支持: tailwindcss-taro-runtime.js')
  }

  /**
   * 查找 WXSS 文件
   */
  private async findWxssFiles(dir: string): Promise<string[]> {
    const files: string[] = []
    
    async function scan(currentDir: string) {
      const items = await fs.readdir(currentDir)
      
      for (const item of items) {
        const fullPath = path.join(currentDir, item)
        const stat = await fs.stat(fullPath)
        
        if (stat.isDirectory()) {
          await scan(fullPath)
        } else if (item.endsWith('.wxss')) {
          files.push(fullPath)
        }
      }
    }
    
    await scan(dir)
    return files
  }

  /**
   * 查找 JS 文件
   */
  private async findJsFiles(dir: string): Promise<string[]> {
    const files: string[] = []
    
    async function scan(currentDir: string) {
      const items = await fs.readdir(currentDir)
      
      for (const item of items) {
        const fullPath = path.join(currentDir, item)
        const stat = await fs.stat(fullPath)
        
        if (stat.isDirectory()) {
          await scan(fullPath)
        } else if (item.endsWith('.js') || item.endsWith('.ts')) {
          files.push(fullPath)
        }
      }
    }
    
    await scan(dir)
    return files
  }

  /**
   * 修复被去除的类名
   */
  private fixRemovedClassNames(content: string): string {
    return content
      // 修复 className 属性
      .replace(/className\s*=\s*["']([^"']*)["']/g, (match, className) => {
        const escapedClasses = className.split(/\s+/).map(escapeWeappClass).join(' ')
        return `className="${escapedClasses}"`
      })
      // 修复 class 属性
      .replace(/class\s*=\s*["']([^"']*)["']/g, (match, className) => {
        const escapedClasses = className.split(/\s+/).map(escapeWeappClass).join(' ')
        return `class="${escapedClasses}"`
      })
  }

  /**
   * 修复选择器兼容性
   */
  private fixSelectorCompatibility(content: string): string {
    return content
      // 替换 :root 为 page
      .replace(/:root/g, 'page')
      // 替换 * 为 view,text
      .replace(/\*/g, 'view,text')
      // 移除不兼容的伪类
      .replace(/:[a-z-]+(?=\s*[.{])/g, '')
      // 移除不兼容的伪元素
      .replace(/::[a-z-]+/g, '')
  }
} 